import type { MedplumClient } from '@medplum/core';
import type { Task } from '@medplum/fhirtypes';
import { PLAN_DEFINITION_SYSTEM } from '../../../src/constants/workflows';

/**
 * Finds and completes a workflow task by its action ID for a given patient.
 * Used by bots that handle automated steps to mark the corresponding workflow task as done.
 *
 * @param medplum - The Medplum client
 * @param patientId - The patient ID
 * @param actionId - The PlanDefinition action ID (e.g., 'flow-hsat-eligibility-algorithm')
 * @returns The completed task, or undefined if no matching task was found
 */
export async function completeWorkflowTask(
  medplum: MedplumClient,
  patientId: string,
  actionId: string
): Promise<Task | undefined> {
  // Find the active workflow task for this action
  const task = await medplum.searchOne('Task', {
    patient: `Patient/${patientId}`,
    code: `${PLAN_DEFINITION_SYSTEM}|${actionId}`,
    status: 'requested,in-progress,draft',
  });

  if (!task) {
    console.log(`[CompleteWorkflowTask] No active task found for action "${actionId}" on Patient/${patientId}`);
    return undefined;
  }

  const updated = await medplum.updateResource<Task>({
    ...task,
    status: 'completed',
    executionPeriod: {
      ...task.executionPeriod,
      end: new Date().toISOString(),
    },
  });

  console.log(`[CompleteWorkflowTask] Completed task "${updated.code?.coding?.[0]?.display}" (${updated.id})`);
  return updated;
}
